/** @jsx jsx */
import React, { useState, useEffect } from "react";
import _ from "lodash";
import { jsx } from "@emotion/core";
import PropTypes from "prop-types";
import {
  Container,
  Grid,
  Dropdown,
  Loader,
  Button,
  Header,
  Icon
} from "semantic-ui-react";
import getStyles from "./recommendation-dashboard-styles";
import StackedBarByDepartment from "./stacked-bar-by-department";
import StackedBarByAudit from "./stacked-bar-by-audit";
import StackedBarByYear from "./stacked-bar-by-year";
import RecommendationTable from "./recommendation-table";
import PieGeneric from "./pie";
import DashboardButton from "./dashboard-button";
import StatColumn from "./stat-column";

import {
  addColorsToBarGraphData,
  addColorsToPieData,
  getGraphColors,
  bluePurpleColors,
  yellowOrangeBrown
} from "./custom-graph-colors";

const RecommendationDashboard = ({ data, loadingData, filterOptions }) => {
  // Constants
  const DROPDOWN_FOCUS_STYLES = `
    /* Dropdown trigger focus */
    .ui.dropdown#recommendation-filters:focus {
      outline: 2px solid #0078d4 !important;
      outline-offset: 2px !important;
    }
    
    /* Menu item hover/selected state */
    #recommendation-filters .menu > .item.selected,
    #recommendation-filters .menu > .item:hover {
      background-color: #e3f2fd !important;
      color: #0078d4 !important;
      font-weight: 600 !important;
      border-left: 3px solid #0078d4 !important;
      padding-left: 17px !important;
    }
  `;

  // State Variables
  const [filter, setFilter] = useState("all");
  const [activeDashboardSection, setActiveDashboardSection] =
    useState("summary");
  const [filterPrimary, setFilterPrimary] = useState([]);
  const [filterSecondary, setFilterSecondary] = useState([]);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("ascending");
  const [statusMessage, setStatusMessage] = useState("");
  const [showDataTables, setShowDataTables] = useState(false);

  // Data Extraction & Derived Values
  const {
    trackSavings,
    dotNotation,
    recommendationStatuses = [],
    recommendationCategories = [],
    recommendationColor = [],
    dashboardTitle = []
  } = data;

  const primaryColorObject = _.find(recommendationColor, {
    name: "Primary Color"
  });
  const primaryColor = _.get(primaryColorObject, "primaryColor");
  const styles = getStyles(primaryColor);
  const graphTextColorObject = _.find(recommendationColor, {
    name: "Graph Text Color"
  });
  const graphTextColor = _.get(graphTextColorObject, "graphTextColor");
  const statusColors = getGraphColors(recommendationStatuses, bluePurpleColors);
  const categoryColors = getGraphColors(
    recommendationCategories,
    yellowOrangeBrown
  );

  const statusKeys = _.map(recommendationStatuses, "name");

  const [{ title: dashboardTitleText, subtitle: dashboardSubtitleText } = {}] =
    dashboardTitle;

  // filtered properties
  const filterData = data[filter] || {};
  const {
    overallStats = [],
    byInvestigationAndStatus = [],
    byDepartmentAndStatus = [],
    byYearAndStatus = [],
    byAuditAndStatus = [],
    byStatus = [],
    byCategory = [],
    recommendationsTable = []
  } = filterData;

  const hasInvestigations = byInvestigationAndStatus.length > 0;
  const isExternal = filterOptions.length === 3;
  // this code is to check that dotNotation exists in the dashboardData object so that in the public dashboard reco numbers can be conditionally rendered
  // there may be old public dashboard data stored in the s3 bucket that will not have dotNotation included. this can be removed eventually.
  const dotNotationExists = !_.isNil(dotNotation);

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0
  });

  const direction = sortDirection === "ascending" ? "asc" : "desc";

  // Table Filtering & Sorting Logic
  const getSortedRecommendationsTable = () => {
    let sortedRecommendationsTable = _.orderBy(
      recommendationsTable,
      ["auditName", "recommendationNumber"],
      [direction, direction]
    );

    if (dotNotation === true) {
      sortedRecommendationsTable = _.orderBy(
        recommendationsTable,
        ["auditName", "findingNumber", "recommendationNumber"],
        [direction, direction, direction]
      );
    }

    return sortedRecommendationsTable;
  };

  const _getFilteredTable = () => {
    const sortedRecommendationsTable = getSortedRecommendationsTable();
    let filteredTable = sortedRecommendationsTable;

    const [primaryKey, primaryValue] = filterPrimary;
    const [secondaryKey, secondaryValue] = filterSecondary;

    if (primaryKey) {
      filteredTable = _.filter(filteredTable, {
        [primaryKey]: primaryValue
      });
    }

    if (secondaryKey) {
      if (secondaryValue === "Unissued") {
        filteredTable = _.filter(filteredTable, item => !item.issueDate);
      } else {
        filteredTable = _.filter(filteredTable, {
          [secondaryKey]: secondaryValue
        });
      }
    }

    if (sortColumn) {
      if (sortColumn === "recommendationNumber" && dotNotation) {
        return _.orderBy(
          filteredTable,
          ["findingNumber", "recommendationNumber"],
          [direction, direction]
        );
      }
      return _.orderBy(filteredTable, [sortColumn], [direction]);
    }

    return filteredTable;
  };

  const _getFilteredText = () => {
    const [, primaryValue] = filterPrimary;
    const [, secondaryValue] = filterSecondary;

    if (!primaryValue) return null;
    if (!secondaryValue) return primaryValue;
    return `${primaryValue} and ${secondaryValue}`;
  };

  const sortedTable = _getFilteredTable();
  const filteredText = _getFilteredText();

  // Chart Data
  const barGraphDataByYear = addColorsToBarGraphData(
    byYearAndStatus,
    statusColors
  );

  const barGraphDataByDepartment = addColorsToBarGraphData(
    byDepartmentAndStatus,
    statusColors
  );

  const sortedByAuditAndStatus = _(byAuditAndStatus)
    .sortBy("totalCount")
    .takeRight(20)
    .value();

  const sortedByInvestigationAndStatus = _(byInvestigationAndStatus)
    .sortBy("totalCount")
    .takeRight(20)
    .value();

  const barGraphDataByAudit = addColorsToBarGraphData(
    sortedByAuditAndStatus,
    statusColors
  );
  const barGraphDataByInvestigation = addColorsToBarGraphData(
    sortedByInvestigationAndStatus,
    statusColors
  );

  const pieDataForStatuses = addColorsToPieData(
    byStatus,
    "status",
    statusColors
  );
  const pieDataForCategories = addColorsToPieData(
    byCategory,
    "category",
    categoryColors
  );

  // Event Handlers
  const announceStatus = message => {
    setStatusMessage(message);
    // Clear after announcement
    setTimeout(() => setStatusMessage(""), 3000);
  };

  const handleButtonClick = section => {
    setActiveDashboardSection(section);
    announceStatus(
      `${section === "summary" ? "Summary" : `By ${section}`} view selected`
    );
  };

  const handleChartClick = (event, primaryFilterKey, secondaryFilterKey) => {
    setFilterPrimary([primaryFilterKey, event.id]);
    setFilterSecondary([secondaryFilterKey, event.indexValue]);

    const newStatusMessage = `Table filtered by ${event.id}${
      event.indexValue ? ` in ${event.indexValue}` : ""
    }`;
    announceStatus(newStatusMessage);

    if (secondaryFilterKey === "Unissued") {
      setFilterSecondary([secondaryFilterKey, null]);
    }
  };

  const handleDataTableButtonFocus = e => {
    e.target.style.outline = "2px solid #0078d4";
    e.target.style.outlineOffset = "2px";
  };

  const handleDataTableButtonBlur = e => {
    e.target.style.outline = "none";
    e.target.style.outlineOffset = "0";
  };

  const resetTable = () => {
    setFilterPrimary([]);
    setFilterSecondary([]);
  };

  const handleSort = clickedColumn => {
    if (sortColumn !== clickedColumn) {
      setSortColumn(clickedColumn);
      setSortDirection("ascending");
      announceStatus(`Table sorted by ${clickedColumn} in ascending order`);

      return;
    }

    const newDirection =
      sortDirection === "ascending" ? "descending" : "ascending";
    setSortDirection(newDirection);
    announceStatus(`Table sorted by ${clickedColumn} in ${newDirection} order`);
  };

  const handleTabNavigation = e => {
    const tabs = ["summary", "department", "audit"];
    if (hasInvestigations) tabs.push("investigation");

    const currentIndex = tabs.indexOf(activeDashboardSection);

    if (e.key === "ArrowRight") {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % tabs.length;
      setActiveDashboardSection(tabs[nextIndex]);

      setTimeout(() => {
        const nextButton = document.getElementById(`${tabs[nextIndex]}-tab`);
        if (nextButton) {
          nextButton.focus();
        }
      }, 0);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      const prevIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
      setActiveDashboardSection(tabs[prevIndex]);

      setTimeout(() => {
        const prevButton = document.getElementById(`${tabs[prevIndex]}-tab`);
        if (prevButton) {
          prevButton.focus();
        }
      }, 0);
    }
  };

  useEffect(() => {
    // Fix ARIA roles after Semantic UI renders
    const dropdown = document.querySelector("#recommendation-filters");
    if (dropdown) {
      const options = dropdown.querySelectorAll('[role="alert"]');
      options.forEach(option => {
        option.setAttribute("role", "option");
      });
    }
  }, [filter]);

  // Component Props
  const paramsRecommendationTable = {
    recommendations: sortedTable,
    handleSort,
    sortColumn,
    sortDirection,
    loadingData,
    activeFilterPrimary: filterPrimary[0],
    activeFilterSecondary: filterSecondary[0],
    dotNotation,
    dotNotationExists
  };

  const buttonProps = {
    activeDashboardSection,
    handleButtonClick,
    handleTabNavigation
  };

  return (
    <Container css={styles} role="main" aria-label="Recommendations Dashboard">
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {statusMessage}
      </div>
      <Grid.Column columns={4}>
        <Grid.Row
          className="reco-filter"
          role="region"
          aria-label="Dashboard filters"
        >
          <Grid.Column width={16} textAlign="center">
            {dashboardTitleText && (
              <Header
                dividing
                textAlign="center"
                style={{
                  fontSize: "1.6rem",
                  marginTop: ".5rem",
                  color: primaryColor || "#3B8163"
                }}
              >
                {dashboardTitleText}
                {dashboardSubtitleText && (
                  <Header.Subheader
                    style={{
                      fontSize: "1.2rem",
                      color: "#707070"
                    }}
                  >
                    {dashboardSubtitleText}
                  </Header.Subheader>
                )}
              </Header>
            )}
            <style>{DROPDOWN_FOCUS_STYLES}</style>
            <span
              id="filter-label"
              style={{ fontSize: "14px", fontWeight: "500" }}
            >
              Showing all
            </span>
            &nbsp;
            <Dropdown
              id="recommendation-filters"
              defaultValue="all"
              inline
              compact
              options={filterOptions}
              onChange={(e, d) => setFilter(d.value)}
              onFocus={e => {
                e.currentTarget.style.outline = "2px solid #0078d4";
                e.currentTarget.style.outlineOffset = "2px";
              }}
              onBlur={e => {
                e.currentTarget.style.outline = "none";
                e.currentTarget.style.outlineOffset = "0";
              }}
              aria-labelledby="filter-label"
              selectOnBlur={false}
            />
            <br />
            {isExternal && (
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  window.open(window.location.href, "_blank");
                }}
                aria-label="View Full Screen (opens in new window)"
                style={{
                  display: "inline-block",
                  minHeight: "24px",
                  minWidth: "24px",
                  padding: "8px 12px",
                  fontSize: "16px",
                  color: "#1E7BC2",
                  fontWeight: "bold"
                }}
              >
                View Full Screen &#128470;
              </a>
            )}
          </Grid.Column>
        </Grid.Row>
        {loadingData && (
          <div role="status" aria-live="polite">
            <Loader active inline="centered">
              Loading
            </Loader>
          </div>
        )}
        {!loadingData && (
          <>
            <Grid.Row
              centered
              className="reco-stats"
              role="region"
              aria-label="Dashboard statistics overview"
            >
              <Grid.Column width={16}>
                <Grid>
                  <Grid.Row centered>
                    {!trackSavings && <Grid.Column width={3} />}
                    <StatColumn
                      id="audit-reports"
                      value={overallStats.auditCount}
                      label="Audit Reports"
                    />
                    {overallStats.investigationCount > 0 && (
                      <StatColumn
                        id="investigations-label"
                        value={overallStats.investigationCount}
                        label="Investigations"
                      />
                    )}
                    <StatColumn
                      id="recommendations-label"
                      value={overallStats.recommendationCount}
                      label="Recommendations"
                    />
                    {!trackSavings && <Grid.Column width={3} />}
                    {trackSavings && (
                      <React.Fragment>
                        <StatColumn
                          id="potential-benefit-label"
                          value={currencyFormatter.format(
                            overallStats.recommendationExpectedBenefit || 0
                          )}
                          label="Potential Benefit"
                        />
                        <StatColumn
                          id="realized-benefit-label"
                          value={currencyFormatter.format(
                            overallStats.recommendationRealizedBenefit || 0
                          )}
                          label="Realized Benefit"
                          className="reco-stat reco-suavé"
                        />
                      </React.Fragment>
                    )}
                  </Grid.Row>
                </Grid>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid
                centered
                className="reco-nav"
                style={{ marginTop: 10 }}
                role="tablist"
                aria-label="Dashboard view selection"
                stackable
              >
                <Grid.Row>
                  <DashboardButton
                    buttonKey="summary"
                    label="Summary"
                    {...buttonProps}
                  />
                  <DashboardButton
                    buttonKey="department"
                    label="By Department"
                    {...buttonProps}
                  />
                  <DashboardButton
                    buttonKey="audit"
                    label="By Audit"
                    {...buttonProps}
                  />
                  {hasInvestigations && (
                    <DashboardButton
                      buttonKey="investigation"
                      label="By Investigation"
                      {...buttonProps}
                    />
                  )}
                </Grid.Row>
              </Grid>
            </Grid.Row>

            {activeDashboardSection === "summary" && (
              <Grid
                className="reco-detailed-stats"
                role="tabpanel"
                id="summary-panel"
                aria-labelledby="summary-tab"
                stackable
              >
                <Grid.Row className="reco-detailed-stats-feature">
                  <Grid.Column width={16}>
                    <div style={{ position: "relative" }}>
                      <Header
                        as="h5"
                        textAlign="center"
                        style={{ color: "#5e5e5e", fontSize: "16px" }}
                        id="year-chart-title"
                      >
                        {" "}
                        Recommendations Issued by Year
                      </Header>
                      <Button
                        onClick={() => setShowDataTables(!showDataTables)}
                        onFocus={handleDataTableButtonFocus}
                        onBlur={handleDataTableButtonBlur}
                        aria-expanded={showDataTables}
                        aria-label={`${
                          showDataTables ? "Hide" : "Show"
                        } all data tables`}
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          minHeight: "24px",
                          minWidth: "24px",
                          fontWeight: "bold",
                          color: primaryColor || "#3B8163",
                          backgroundColor: "white",
                          border: `2px solid ${primaryColor || "#3B8163"}`,
                          borderRadius: "6px"
                        }}
                      >
                        <Icon
                          name={showDataTables ? "angle down" : "angle right"}
                        />
                        {showDataTables ? "Hide" : "Show"} Data Tables
                      </Button>
                    </div>

                    <StackedBarByYear
                      {...{
                        barGraphDataByYear,
                        statusKeys,
                        handleChartClick,
                        graphTextColor,
                        showDataTables,
                        styles
                      }}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Header
                      as="h5"
                      textAlign="center"
                      style={{ color: "#5e5e5e", fontSize: "16px" }}
                      id="category-chart-title"
                    >
                      {" "}
                      Recommendations by Category
                    </Header>
                    <PieGeneric
                      {...{
                        data: pieDataForCategories,
                        handleChartClick,
                        clickType: "category",
                        graphTextColor,
                        showDataTables,
                        styles
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Header
                      as="h5"
                      textAlign="center"
                      style={{ color: "#5e5e5e", fontSize: "16px" }}
                      id="status-chart-title"
                    >
                      {" "}
                      Recommendations by Status
                    </Header>
                    <PieGeneric
                      {...{
                        data: pieDataForStatuses,
                        handleChartClick,
                        clickType: "status",
                        graphTextColor,
                        showDataTables,
                        styles
                      }}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            )}

            {activeDashboardSection === "department" && (
              <Grid
                className="reco-detailed-stats"
                role="tabpanel"
                id="department-panel"
                aria-labelledby="department-tab"
              >
                <Grid.Row className="reco-detailed-stats-feature">
                  <Grid.Column width={16}>
                    <div style={{ position: "relative" }}>
                      <Header
                        as="h5"
                        textAlign="center"
                        style={{ color: "#5e5e5e", fontSize: "16px" }}
                        id="department-chart-title"
                      >
                        {" "}
                        Recommendations by Department and Status
                      </Header>
                      <Button
                        onClick={() => setShowDataTables(!showDataTables)}
                        onFocus={handleDataTableButtonFocus}
                        onBlur={handleDataTableButtonBlur}
                        aria-expanded={showDataTables}
                        aria-label={`${
                          showDataTables ? "Hide" : "Show"
                        } all data tables`}
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          minHeight: "24px",
                          minWidth: "24px",
                          fontWeight: "bold",
                          color: primaryColor || "#3B8163",
                          backgroundColor: "white",
                          border: `2px solid ${primaryColor || "#3B8163"}`,
                          borderRadius: "6px"
                        }}
                      >
                        <Icon
                          name={showDataTables ? "angle down" : "angle right"}
                        />
                        {showDataTables ? "Hide" : "Show"} Data Table
                      </Button>
                    </div>
                    <StackedBarByDepartment
                      {...{
                        barGraphDataByDepartment,
                        statusKeys,
                        handleChartClick,
                        graphTextColor,
                        showDataTables,
                        styles
                      }}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            )}

            {activeDashboardSection === "audit" && (
              <Grid
                className="reco-detailed-stats"
                role="tabpanel"
                id="audit-panel"
                aria-labelledby="audit-tab"
              >
                <Grid.Row className="reco-detailed-stats-feature">
                  <Grid.Column width={16}>
                    <div style={{ position: "relative" }}>
                      <Header
                        as="h5"
                        textAlign="center"
                        style={{ color: "#5e5e5e", fontSize: "16px" }}
                        id="audit-chart-title"
                      >
                        {" "}
                        Recommendations Issued by Audit (Top 20)
                      </Header>
                      <Button
                        onClick={() => setShowDataTables(!showDataTables)}
                        onFocus={handleDataTableButtonFocus}
                        onBlur={handleDataTableButtonBlur}
                        aria-expanded={showDataTables}
                        aria-label={`${
                          showDataTables ? "Hide" : "Show"
                        } all data tables`}
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          minHeight: "24px",
                          minWidth: "24px",
                          fontWeight: "bold",
                          color: primaryColor || "#3B8163",
                          backgroundColor: "white",
                          border: `2px solid ${primaryColor || "#3B8163"}`,
                          borderRadius: "6px"
                        }}
                      >
                        <Icon
                          name={showDataTables ? "angle down" : "angle right"}
                        />
                        {showDataTables ? "Hide" : "Show"} Data Table
                      </Button>
                    </div>
                    <StackedBarByAudit
                      {...{
                        barGraphDataByAudit,
                        statusKeys,
                        handleChartClick,
                        graphTextColor,
                        showDataTables,
                        chartType: "audit",
                        styles
                      }}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            )}

            {activeDashboardSection === "investigation" && (
              <Grid
                className="reco-detailed-stats"
                role="tabpanel"
                id="investigation-panel"
                aria-labelledby="investigation-tab"
              >
                <Grid.Row className="reco-detailed-stats-feature">
                  <Grid.Column width={16}>
                    <div style={{ position: "relative" }}>
                      <Header
                        as="h5"
                        textAlign="center"
                        style={{ color: "#5e5e5e", fontSize: "16px" }}
                        id="investigation-chart-title"
                      >
                        {" "}
                        Recommendations Issued by Investigation (Top 20)
                      </Header>
                      <Button
                        onClick={() => setShowDataTables(!showDataTables)}
                        onFocus={handleDataTableButtonFocus}
                        onBlur={handleDataTableButtonBlur}
                        aria-expanded={showDataTables}
                        aria-label={`${
                          showDataTables ? "Hide" : "Show"
                        } all data tables`}
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          minHeight: "24px",
                          minWidth: "24px",
                          fontWeight: "bold",
                          color: primaryColor || "#3B8163",
                          backgroundColor: "white",
                          border: `2px solid ${primaryColor || "#3B8163"}`,
                          borderRadius: "6px"
                        }}
                      >
                        <Icon
                          name={showDataTables ? "angle down" : "angle right"}
                        />
                        {showDataTables ? "Hide" : "Show"} Data Table
                      </Button>
                    </div>
                    <StackedBarByAudit
                      {...{
                        barGraphDataByAudit: barGraphDataByInvestigation,
                        statusKeys,
                        handleChartClick,
                        graphTextColor,
                        showDataTables,
                        chartType: "investigation"
                      }}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            )}
            <Grid.Row width={8}>
              <Grid.Column width={10}>
                <div
                  className="reco-detailed-stats-header"
                  role="region"
                  aria-labelledby="table-heading"
                >
                  <Header as="h3" textAlign="left" id="table-heading">
                    {" "}
                    Recommendation List
                    <Header.Subheader style={{ color: "#707070" }}>
                      Select any chart segment to filter this table.
                    </Header.Subheader>
                  </Header>

                  {filteredText && (
                    <React.Fragment>
                      <br />
                      <br />
                      <div>
                        <span role="status" aria-live="polite">
                          <strong>Filtered by: </strong>
                          {filteredText}
                        </span>
                        &nbsp;&nbsp;
                        <Button
                          compact
                          icon
                          labelPosition="left"
                          onClick={resetTable}
                          onFocus={handleDataTableButtonFocus}
                          onBlur={handleDataTableButtonBlur}
                          aria-label="Reset Table - Clear all table filters and show all recommendations"
                          style={{
                            minHeight: "24px",
                            minWidth: "24px",
                            fontSize: "14px"
                          }}
                        >
                          <Icon name="undo alternate" />
                          Reset Table
                        </Button>
                      </div>
                    </React.Fragment>
                  )}

                  <RecommendationTable {...paramsRecommendationTable} />
                </div>
              </Grid.Column>
            </Grid.Row>
          </>
        )}
      </Grid.Column>
    </Container>
  );
};

RecommendationDashboard.propTypes = {
  data: PropTypes.shape({
    trackSavings: PropTypes.bool,
    dotNotation: PropTypes.bool,
    recommendationStatuses: PropTypes.arrayOf(PropTypes.shape({})),
    recommendationCategories: PropTypes.arrayOf(PropTypes.shape({})),
    recommendationColor: PropTypes.arrayOf(PropTypes.shape({})),
    all: PropTypes.shape({}),
    open: PropTypes.shape({}),
    closed: PropTypes.shape({}),
    draft: PropTypes.shape({}),
    draftWithPublished: PropTypes.shape({}),
    dashboardTitle: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  loadingData: PropTypes.bool.isRequired,
  filterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired
    })
  ).isRequired
};

export default RecommendationDashboard;
